'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import type { Media } from '@payload-types';
import type { SubjectsById } from '../subjects-by-id';
import { MediaElement } from '../media-element';
import { PORTFOLIO_COMPARISON_DEFAULT_PCT, PortfolioComparison } from './portfolio-comparison';

export type PortfolioLightboxItem = {
  id: string;
  title: string;
  summary: string | null;
  image: Media;
  before: Media | null;
  after: Media | null;
  /** Pre-joined meta line, e.g. "Priya Mehta · September 2026". The caller decides its parts. */
  meta: string | null;
  quote: string | null;
  reviewer: string | null;
};

/** The modal box is `max-w-5xl` (64rem = 1024px) from `xl` up. */
const LIGHTBOX_IMAGE_SIZES = '(min-width: 1280px) 1024px, 95vw';
const NAV_BUTTON = 'btn btn-circle absolute top-1/2 z-40 -translate-y-1/2 bg-base-100/90';

/**
 * The photo stage for one item. The caller keys it by item id, so the
 * before/after divider starts at centre for every item — unlike the filmstrip,
 * whose divider is shared with the tile behind it and so persists.
 */
const LightboxStage: React.FC<{ item: PortfolioLightboxItem; subjects: SubjectsById | undefined }> = ({ item, subjects }) => {
  const [pct, setPct] = useState(PORTFOLIO_COMPARISON_DEFAULT_PCT);

  if (item.before !== null && item.after !== null) {
    return (
      <PortfolioComparison
        variant="lightbox"
        before={item.before}
        after={item.after}
        image={item.image}
        pct={pct}
        onPct={setPct}
        title={item.title}
        aspect="size-full"
        sizes={LIGHTBOX_IMAGE_SIZES}
        subjects={subjects}
      />
    );
  }

  return (
    <MediaElement
      data={item.image}
      fill
      sizes={LIGHTBOX_IMAGE_SIZES}
      className="object-contain object-center"
      subject={subjects?.[item.image.id]}
    />
  );
};

/**
 * The shared portfolio lightbox: a native modal `dialog`, stepping
 * through `items` with prev/next buttons and the ← / → keys.
 *
 * CONTROLLED by `openIndex`: the component that renders the tiles owns which
 * item is open (the brick grid also pauses its marquee on it), and `null`
 * closes. The native dialog FOLLOWS the prop — the ref callback below opens it when
 * an item is chosen and closes it when `openIndex` goes back to `null`,
 * whoever cleared it.
 *
 * The dialog stays beside its trigger in the DOM. `showModal()` promotes it to
 * the browser's top layer, escaping clipping while preserving inherited theme
 * values. Disabling the dialog's visibility transition keeps DaisyUI's hidden
 * starting style from delaying native focus on a first lazy mount. Native dialog
 * focus handling opens on Close and returns focus to the trigger; no portal,
 * animation-frame retries or custom focus trap is needed.
 * Arrow keys stay with the comparison range while it is focused; modified
 * arrows stay with the browser (Alt+← is Back).
 *
 * Every close reports `null` exactly once, from wherever it is first known and
 * always synchronously: Close and the backdrop from native dialog-form submissions, Esc from the
 * dialog's `cancel` event. Nothing waits on the native `close` event — browsers
 * queue it as a task (in a throttled tab it has landed ~600 ms late), so a
 * reopen can overtake it, and a run of closes and reopens can leave several of
 * those events in flight at once.
 *
 * `close` is only the fallback for a close nobody reported — the browser
 * closing the dialog without a `cancel` (Chrome skips `cancel` on a repeated
 * Esc with no user activation in between). It ignores an event delivered while
 * the dialog is open again (a reopen overtook it), and reports nothing once
 * `openIndex` is already `null` (the echo of a close that was reported). Both
 * checks read the state at delivery time, so they hold however many events are
 * queued — the "expected echo" flag this replaces covered only one.
 */
export const PortfolioLightbox: React.FC<{
  id: string;
  items: readonly PortfolioLightboxItem[];
  openIndex: number | null;
  onOpenIndexChange: (index: number | null) => void;
  showQuotes?: boolean;
  /**
   * The Subjects from the server caller (`portfolioSubjects`): each item's by
   * item id, spread on the open item, and each photo's by Media id.
   */
  subjects?: SubjectsById;
}> = ({ id, items, openIndex, onOpenIndexChange, showQuotes = true, subjects }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // `Array.prototype.at` counts back from the end for a negative index, so a
  // caller landing on `-1` (e.g. a `findIndex` miss) must be excluded here or
  // it would silently open the LAST item instead of staying closed.
  const item = openIndex === null || openIndex < 0 ? null : items.at(openIndex) ?? null;
  const isOpen = item !== null;
  const hasPrev = openIndex !== null && openIndex > 0;
  const hasNext = openIndex !== null && openIndex < items.length - 1;

  const syncDialog = useCallback((dialog: HTMLDialogElement | null) => {
    dialogRef.current = dialog;
    if (dialog === null) return;
    if (isOpen) {
      if (!dialog.open) dialog.showModal();
    } else if (dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Stepping to an end removes the arrow that was just pressed; keep focus
  // inside the dialog rather than letting it fall to <body>.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (openIndex === null || dialog === null || !dialog.open) return;
    if (!dialog.contains(document.activeElement)) closeRef.current?.focus();
  }, [openIndex]);

  const go = (delta: -1 | 1) => {
    if (openIndex === null) return;
    const next = openIndex + delta;
    if (next >= 0 && next < items.length) onOpenIndexChange(next);
  };

  /**
   * Tells the parent the lightbox closed — unless nothing is open, which is
   * also how a late echo of an already-reported close ends. The native dialog
   * then closes through the ref callback above, once the parent clears the index.
   */
  const reportClosed = () => {
    if (openIndex !== null) onOpenIndexChange(null);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    if (event.target instanceof HTMLInputElement) return;
    if (event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(1);
    }
  };

  return (
    <dialog
      id={id}
      ref={syncDialog}
      className="modal transition-none"
      aria-label={item === null ? 'Expanded portfolio image' : item.title}
      onCancel={reportClosed}
      onClose={(event) => {
        // Open again: a reopen overtook this queued event, whose close is long over.
        if (event.currentTarget.open) return;
        reportClosed();
      }}
      onKeyDown={onKeyDown}
    >
      <div className="modal-box max-w-5xl overflow-auto p-0 motion-reduce:transition-none">
        <form method="dialog" onSubmit={reportClosed}>
          <button
            ref={closeRef}
            type="submit"
            autoFocus
            aria-label="Close"
            className="btn btn-circle btn-sm absolute right-3 top-3 z-40 bg-base-100/90"
          >
            <X className="size-4" aria-hidden />
          </button>
        </form>

        {item === null || openIndex === null ? null : (
          <figure {...subjects?.[item.id]}>
            <div className="relative h-lightbox min-h-64 bg-black">
              <LightboxStage key={item.id} item={item} subjects={subjects} />
              {hasPrev ? (
                <button
                  type="button"
                  aria-label="Previous photo"
                  onClick={() => go(-1)}
                  className={`${NAV_BUTTON} left-3`}
                >
                  <ArrowLeft className="size-4" aria-hidden />
                </button>
              ) : null}
              {hasNext ? (
                <button
                  type="button"
                  aria-label="Next photo"
                  onClick={() => go(1)}
                  className={`${NAV_BUTTON} right-3`}
                >
                  <ArrowRight className="size-4" aria-hidden />
                </button>
              ) : null}
            </div>
            <figcaption className="flex flex-col gap-2 p-5 sm:p-6">
              {item.meta !== null || items.length > 1 ? (
                <div className="flex items-baseline gap-4">
                  {item.meta !== null ? (
                    <p className="text-xs font-semibold uppercase tracking-widest text-primary">{item.meta}</p>
                  ) : null}
                  {items.length > 1 ? (
                    <p role="status" aria-atomic="true" className="ml-auto text-xs tabular-nums text-base-content/70">
                      <span className="sr-only">{item.title}: </span>
                      {openIndex + 1} of {items.length}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <h3 className="text-2xl font-semibold leading-snug text-balance">{item.title}</h3>
              {item.summary ? (
                <p className="max-w-3xl leading-relaxed text-base-content/70">{item.summary}</p>
              ) : null}
              {showQuotes && item.quote ? (
                <blockquote className="mt-1 border-t border-base-content/15 pt-3 italic text-base-content/80">
                  <p>&ldquo;{item.quote}&rdquo;</p>
                  {item.reviewer ? (
                    <p className="mt-1 text-sm not-italic text-base-content/70">{item.reviewer}</p>
                  ) : null}
                </blockquote>
              ) : null}
            </figcaption>
          </figure>
        )}
      </div>

      <form method="dialog" className="modal-backdrop" onSubmit={reportClosed} aria-hidden="true">
        <button type="submit" tabIndex={-1}>close</button>
      </form>
    </dialog>
  );
};
